"use client";

import type * as React from "react";
import { useMemo } from "react";
import Image from "next/image";
import type { Vehicle } from "@/lib/vehicles";
import {
  estimateLuggageCapacity,
  extractYearRange,
  formatAud,
  getFuelTypeLabel,
  getSimilarModelLabel,
  getTransmissionLabel,
  getVehicleDisplayName,
  sanitizeImageUrl,
} from "@/lib/vehicles";
import { buildFuelTypeMap, buildTransmissionTypeMap } from "@/lib/master-data";
import { useAppSelector } from "@/lib/redux/hooks";
import { selectMasterData } from "@/lib/redux/features/master-data/masterDataSlice";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  LucideCheck,
  LucideFuel,
  LucideLuggage,
  LucideMapPinCheck,
  LucideMapPinCheckInside,
  LucideUser,
} from "lucide-react";
import { cn } from "@/lib/utils";

type VehicleCardProps = {
  vehicle: Vehicle;
  isSelected?: boolean;
  onSelect?: (vehicle: Vehicle) => void;
};

export const VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  isSelected = false,
  onSelect,
}) => {
  const masterData = useAppSelector(selectMasterData);
  const fuelTypeMap = useMemo(
    () => (masterData ? buildFuelTypeMap(masterData.fuel_types) : undefined),
    [masterData],
  );
  const transmissionTypeMap = useMemo(
    () =>
      masterData
        ? buildTransmissionTypeMap(masterData.transmissions)
        : undefined,
    [masterData],
  );
  const title = getVehicleDisplayName(vehicle);
  const yearRange = extractYearRange(vehicle.similar_vehicle_name);
  const similarLabel = getSimilarModelLabel(vehicle);
  const fuelLabel = getFuelTypeLabel(vehicle.fuel_types, fuelTypeMap);
  const transmissionLabel = getTransmissionLabel(
    vehicle.transmission_types,
    transmissionTypeMap,
  );
  const isAutomatic = vehicle.transmission_types === "1";
  const luggageCount = estimateLuggageCapacity(vehicle.no_of_passengers);
  const imageUrl = sanitizeImageUrl(vehicle.similar_car_image);
  const dayRate = vehicle.rates?.day_rate;
  const totalRate = vehicle.rates?.total;

  return (
    <article
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(vehicle) : undefined}
      onKeyDown={
        onSelect
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(vehicle);
              }
            }
          : undefined
      }
      className={cn(
        "relative flex h-full min-h-120 flex-col rounded-2xl bg-linear-to-t from-slate-500 to-slate-900 p-6 outline-offset-4 hover:outline-4 hover:outline-slate-400",
        onSelect && "cursor-pointer transition-shadow",
        isSelected && "z-10 outline-4 outline-blue-500 hover:outline-blue-500",
        // showDetailsCaret && "rounded-b-none",
      )} 
    >
      {/* {showDetailsCaret ? (
        <span
          aria-hidden="true"
          className="absolute -bottom-px left-1/2 z-20 size-0 -translate-x-1/2 border-x-10 border-t-10 border-x-transparent border-t-orange-500"
        />
      ) : null} */}
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col">
          <div className="flex items-baseline gap-2">
            <h3 className="text-2xl font-semibold text-slate-200">{title}</h3>
            {yearRange ? (
              <span className="text-sm text-slate-300">{yearRange}</span>
            ) : null}
          </div>
          <div className="-mt-1 flex items-center gap-2">
            <Tooltip>
              <span className="text-xs italic text-slate-400">
                {similarLabel}
                {similarLabel !== title ? " or similar" : ""}
              </span>
              <TooltipTrigger>
                <i
                  className="bi bi-info-circle ml-1 text-sm text-slate-400"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>Selected vehicle or similar, based on availability</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="mt-0.5 pt-1 flex flex-wrap gap-x-4 border-t border-slate-600">
            <div className="inline-flex h-6 items-center ">
              <LucideLuggage
                size={18}
                className="text-slate-500"
                aria-hidden="true"
              />
              <span className="ml-1.5 text-xs text-slate-300">
                {luggageCount} Suitcases
              </span>
            </div>
            <div className="inline-flex h-6 items-center">
              <LucideUser
                size={18}
                className="text-slate-500"
                aria-hidden="true"
              />
              <span className="ml-1.5 text-xs text-slate-300">
                {vehicle.no_of_passengers} Seats
              </span>
            </div>
            <div className="inline-flex h-6 items-center">
              <LucideFuel
                size={18}
                className="text-slate-500"
                aria-hidden="true"
              />
              <span className="ml-1.5 text-xs text-slate-300">{fuelLabel}</span>
            </div>
            {transmissionLabel !== "—" ? (
              <div className="inline-flex h-6 items-center">
                {isAutomatic ? (
                  <span className="flex size-4.5 items-center justify-center rounded border border-current text-xs font-bold text-slate-500">
                    A
                  </span>
                ) : (
                  <Image
                    src="/icon/gearshift.svg"
                    alt=""
                    width={16}
                    height={16}
                    aria-hidden="true"
                  />
                )}
                <span className="ml-1.5 text-xs text-slate-300">
                  {transmissionLabel}
                </span>
              </div>
            ) : null}
            <div className="inline-flex h-6 items-center">
              <Image
                src="/icon/car-door.svg"
                alt=""
                width={18}
                height={18}
                aria-hidden="true"
              />
              <span className="ml-1.5 text-xs text-slate-300">
                {vehicle.no_of_doors} Doors
              </span>
            </div>
          </div>

          <div className="mt-2 flex items-end gap-1">
            <LucideMapPinCheckInside size={20} className="text-green-600" />
            <span className="text-sm text-gray-300">Unlimited Distance</span>
          </div>
        </div>

        <div className="relative my-4 flex min-h-[140px] flex-1 items-center justify-center">
          {imageUrl ? (
            <Image
              src={imageUrl}
              // src="/vitz-2.png"
              alt={similarLabel}
              width={320}
              height={300}
              className="max-h-100 w-auto object-contain"
              unoptimized
            />
          ) : null}
        </div>

        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full outline-2 outline-offset-3",
            isSelected
              ? "bg-blue-500 outline-blue-500"
              : "bg-slate-400 outline-slate-400",
          )}
        >
          {isSelected ? (
            <LucideCheck size={20} className="text-white" />
          ) : null}
        </div>

        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-baseline gap-0.5 text-white">
            <span className="font-medium text-slate-300">AU$&nbsp;</span>
            <p className="text-2xl font-bold">
              {dayRate !== undefined ? formatAud(dayRate) : "—"}
            </p>
            <span className="text-xs text-slate-200">/ day</span>
          </div>

          <div>
            <span className="text-slate-300">
              Total : AU${" "}
              <b>{totalRate !== undefined ? formatAud(totalRate) : "—"}</b>
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};
